import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Typography,
  Row,
  Col,
  Statistic,
  Select,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined } from '@ant-design/icons';
import { useStageInspectionsAll } from '../../hooks/useQCWorkflow';
import type { StageCompletionInspection } from '../../services/qc-workflow.service';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

type StageRow = {
  id: string;
  inspection_number: string;
  inspection_source: string;
  description: string;
  work_order_label: string;
  date?: string | null;
  status: string;
  result: string;
  inspector_label: string;
  created_at?: string;
};

const InspectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');

  const { data: allInspections = [], isLoading } = useStageInspectionsAll(
    statusFilter ? { status: statusFilter } : undefined
  );

  const stageRows: StageRow[] = useMemo(
    () =>
      (allInspections as StageCompletionInspection[]).map((i) => {
        const src = i.inspection_source || 'stage';
        const base =
          i.workOrderStage?.stage_name || i.workOrderStage?.stage_code || i.inspection_category || '-';
        const pu = i.workOrderStage?.production_unit_number;
        return {
          id: i.id,
          inspection_number: i.inspection_number,
          inspection_source: src === 'quality_plan' ? 'quality_plan' : 'stage',
          description: pu != null ? `${base} · Unit ${pu}` : base,
          work_order_label: i.workOrder?.work_order_number ?? '-',
          date: i.inspection_date || i.scheduled_date || i.created_at,
          status: i.status,
          result: i.result,
          inspector_label: i.inspector?.name || i.inspector?.email || '-',
          created_at: i.created_at,
        };
      }),
    [allInspections]
  );

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Pending: 'default',
      Scheduled: 'blue',
      'In Progress': 'processing',
      'Pending Approval': 'gold',
      Completed: 'success',
      Failed: 'error',
      'On Hold': 'warning',
      Cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const getResultColor = (result: string) => {
    const colors: Record<string, string> = {
      Pass: 'success',
      Fail: 'error',
      Conditional: 'warning',
      Pending: 'default',
    };
    return colors[result] || 'default';
  };

  const filteredInspections = useMemo(() => {
    let result = stageRows;

    if (sourceFilter) {
      result = result.filter((i) => i.inspection_source === sourceFilter);
    }

    if (statusFilter) {
      result = result.filter((i) => i.status === statusFilter);
    }

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter(
        (i) =>
          i.inspection_number.toLowerCase().includes(q) ||
          i.work_order_label.toLowerCase().includes(q) ||
          i.description.toLowerCase().includes(q)
      );
    }

    return result;
  }, [stageRows, searchText, sourceFilter, statusFilter]);

  const stats = useMemo(() => {
    const pending = stageRows.filter(
      (i) => i.status === 'Pending' || i.status === 'Scheduled'
    ).length;
    const inProgress = stageRows.filter((i) => i.status === 'In Progress').length;
    const completed = stageRows.filter((i) => i.status === 'Completed').length;
    return {
      total: stageRows.length,
      pending,
      inProgress,
      completed,
    };
  }, [stageRows]);

  const columns: ColumnsType<StageRow> = [
    {
      title: 'Source',
      key: 'source',
      width: 130,
      render: (_, row) => {
        const src = row.inspection_source;
        const label = src === 'quality_plan' ? 'Quality Plan' : 'Stage';
        const color = src === 'quality_plan' ? 'purple' : 'blue';
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Inspection No.',
      dataIndex: 'inspection_number',
      key: 'inspection_number',
      width: 160,
    },
    {
      title: 'Operation / Stage',
      dataIndex: 'description',
      key: 'description',
      width: 200,
    },
    {
      title: 'Work Order',
      dataIndex: 'work_order_label',
      key: 'work_order',
      width: 160,
    },
    {
      title: 'Date',
      key: 'date',
      width: 130,
      render: (_, row) => (row.date ? dayjs(row.date).format('DD/MM/YYYY') : '-'),
      sorter: (a, b) =>
        new Date(a.date || a.created_at || 0).getTime() -
        new Date(b.date || b.created_at || 0).getTime(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Result',
      dataIndex: 'result',
      key: 'result',
      width: 100,
      render: (result: string) => <Tag color={getResultColor(result)}>{result}</Tag>,
    },
    {
      title: 'Inspector',
      dataIndex: 'inspector_label',
      key: 'inspector',
      width: 120,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, row) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => navigate(`/qms/inspections/${row.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2} style={{ marginBottom: 4 }}>
        Stage Inspections
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Work-order / line stage inspections. GRN receiving QC is under{' '}
        <a onClick={() => navigate('/scm/procurement/receiving-inspections')}>Receiving Inspections</a>.
      </Text>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="Total" value={stats.total} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Pending" value={stats.pending} valueStyle={{ color: '#145A54' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="In Progress" value={stats.inProgress} valueStyle={{ color: '#fa8c16' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Completed" value={stats.completed} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      <Card>
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Search
              placeholder="Search by inspection number, WO, stage..."
              allowClear
              style={{ width: 300 }}
              onSearch={setSearchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Select
              placeholder="Status"
              allowClear
              style={{ width: 140 }}
              value={statusFilter || undefined}
              onChange={(v) => setStatusFilter(v || '')}
            >
              <Option value="Pending">Pending</Option>
              <Option value="Scheduled">Scheduled</Option>
              <Option value="In Progress">In Progress</Option>
              <Option value="Pending Approval">Pending Approval</Option>
              <Option value="Completed">Completed</Option>
              <Option value="Failed">Failed</Option>
              <Option value="On Hold">On Hold</Option>
            </Select>
            <Select
              placeholder="Source"
              allowClear
              style={{ width: 160 }}
              value={sourceFilter || undefined}
              onChange={(v) => setSourceFilter(v || '')}
            >
              <Option value="stage">Stage</Option>
              <Option value="quality_plan">Quality Plan</Option>
            </Select>
          </Space>
        </Space>

        <Table
          columns={columns}
          dataSource={filteredInspections}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: 1200 }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Card>
    </div>
  );
};

export default InspectionsPage;
